import { useEffect, useState } from 'react';
import { z } from 'zod';
import {
	EmptyValidationMessage,
	IFormSchema,
	addDays,
	addMinutes,
	getIsoDateTime,
	getNow,
	getTzTodayDate,
	isAfter,
	isBefore,
	mapDatePicker,
	parseIsoDateTime,
	useReactForm,
} from '@ninja/utilities';
import { GlobalAPIError, policySave } from '@shory-health-individual/customer-api';
import { useAppPlatformContextOptions } from '../../../../contexts/app';
import { AppInit, JourneyDetails } from '../../../../server';
import { useSystemConfigurationSettings } from '../../../app-init-data';

type PolicyEffectiveForm = {
	policyEffectiveDate?: string;
	policyEndDate?: string;
};

type PolicyEffectiveFormProps = {
	journey: JourneyDetails;
	userDocumentAcknowledgement: ReturnType<AppInit['getUserAcknowledgementsByTypeName']>;
	UserEmirateAcknowledgement: ReturnType<AppInit['getUserAcknowledgementsByTypeName']>;
	UserSalaryAcknowledgement: ReturnType<AppInit['getUserAcknowledgementsByTypeName']>;
	onSuccess: () => void;
	isEidFlow: boolean;
};

const parseEffectiveDate = (effectiveDate: string) => {
	const date = effectiveDate ? parseIsoDateTime(effectiveDate) : undefined;

	if (!date) return '';

	const isBeforeToday = isBefore(date, getTzTodayDate());

	if (isBeforeToday) return '';

	return effectiveDate;
};

export const usePolicyEffectiveDateForm = ({
	journey,
	isEidFlow,
	onSuccess,
	userDocumentAcknowledgement,
	UserEmirateAcknowledgement,
	UserSalaryAcknowledgement,
}: PolicyEffectiveFormProps) => {
	const { translate: t, apiConnectionOptions, onGeneralErrorHandling } = useAppPlatformContextOptions();
	const { systemConfigurationSettings } = useSystemConfigurationSettings();

	const [checkboxStatus, setCheckboxStatus] = useState({ isChecked: false, hasError: false });

	const form = useReactForm<PolicyEffectiveForm>({
		initialValues: {
			policyEffectiveDate: isEidFlow ? journey.effectiveDateFrom : parseEffectiveDate(journey.effectiveDateFrom ?? ''),
		},
		validationMode: 'zod',
		validation: () => {
			return z.object({
				policyEffectiveDate: isEidFlow
					? z.string().optional()
					: z
							.string({
								required_error: EmptyValidationMessage,
							})
							.refine(
								(value) => {
									if (
										(value && isBefore(parseIsoDateTime(value) as Date, getTzTodayDate())) ||
										(value && isAfter(parseIsoDateTime(value) as Date, addDays(getTzTodayDate(), 7))) ||
										!value
									)
										return false;

									return true;
								},
								{ message: EmptyValidationMessage }
							),
				policyEndDate: z.string().optional(),
			});
		},
		validateBehavior: 'on-submit',
		onFormSubmit: async (formValues) => {
			if (!checkboxStatus?.isChecked) {
				setCheckboxStatus({ isChecked: checkboxStatus?.isChecked, hasError: true });

				return;
			}

			try {
				await policySave(apiConnectionOptions)({
					encryptedJourneyId: journey.encryptedJourneyId,
					...(isEidFlow
						? []
						: {
								policyEffectiveDate: formValues.policyEffectiveDate ?? '',
								policyEndDate: formValues.policyEndDate ?? '',
						  }),
					userAcknowledgementIds: [
						userDocumentAcknowledgement?.id,
						UserSalaryAcknowledgement?.id,
						UserEmirateAcknowledgement?.id,
					],
				});

				onSuccess();
			} catch (error) {
				await onGeneralErrorHandling(error as GlobalAPIError);
			}
		},
	});

	const policyStartDate = form.getValues('policyEffectiveDate');

	useEffect(() => {
		if (policyStartDate) {
			form.setFieldValue(
				'policyEndDate',
				getIsoDateTime(
					addMinutes(
						addDays(parseIsoDateTime(policyStartDate) as Date, systemConfigurationSettings?.policyEndDateCutoff ?? 0),
						systemConfigurationSettings?.policyEndDateCutoff ? -1 : 0
					)
				)
			);
		} else {
			form.setFieldValue('policyEndDate', undefined);
		}
	}, [policyStartDate, systemConfigurationSettings?.policyEndDateCutoff]);

	const fields: IFormSchema<PolicyEffectiveForm>['fields'] = [
		mapDatePicker({
			field: 'policyEffectiveDate',
			label: t('documents-review:policy-effective-date'),
			placeholder: t('documents-review:select'),
			fromDate: getNow(),
			toDate: addDays(getNow(), systemConfigurationSettings?.policyEffectiveDateThreshold ?? 0),
			defaultMonth: getNow(),
			isClearable: false,
			size: 'large',
			readonly: true,
			disabled: isEidFlow,
		}),
		mapDatePicker({
			field: 'policyEndDate',
			label: t('documents-review:policy-end-date'),
			placeholder: t('documents-review:select'),
			disabled: true,
			size: 'large',
		}),
	];

	const onDisclaimerChange = () => {
		setCheckboxStatus({
			isChecked: !checkboxStatus?.isChecked,
			hasError: false,
		});
	};

	return {
		fields,
		form,
		checkboxStatus,
		onDisclaimerChange,
	};
};
